import React, { FC } from 'react'
import { Redirect, useLocation } from 'react-router'

import { routes } from '@sketch/modules-common'
import { ErrorHandler } from '@sketch/tracing'
import { useClearUrlQuery, useSearch } from '@sketch/utils'
import {
  DocumentHead,
  LoadingState,
  Pill,
  useForTablet,
  Tabs,
  Tab,
  TabList,
  TabPanel,
} from '@sketch/components'

// TODO; Given that we are removing DocumentItemsLayout, move this component
// to another place
import { Footer } from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'
import ProjectHeader from '../ProjectHeader'
import ProjectEmptyState from '../ProjectEmptyState'
import ProjectCollectionError, {
  ProjectNotFoundError,
} from '../ProjectCollectionError'
import {
  HeaderAfterCollections,
  DocumentsWrapper,
  TabPanelContent,
  MobileWrapper,
} from './ProjectShares.styles'

import { useProject } from '../../hooks/useProject/useProject'

import {
  WorkspaceMinimalFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { ScrollToTop } from '../../../../components/ScrollToTopButton'
import NestedProjectItemsLayout from '../../../nestedProjects/components/NestedProjectItemsLayout'
import NestedProjectDropdown from '../../../nestedProjects/components/NestedProjectDropdown'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { isProject, isShare } from '../../../storageItems/utils/filterTypes'
import { ProjectDocumentList } from './ProjectDocumentList'
import { ResponsiveValues } from '@sketch/global-styles'

const NESTED_PROJECTS_LAYOUT_COLUMNS: ResponsiveValues<number> = [
  1, 1, 2, 2, 2, 3, 4, 5,
]

export interface ProjectSharesProps {
  projectId: string
  workspace: WorkspaceMinimalFragment
}

const ProjectShares: FC<ProjectSharesProps> = props => {
  const { workspace, projectId } = props
  const location = useLocation()
  const { searchDebounced: search } = useSearch()
  const { filters, hasFilters: hasFilterTags } = useStorageItemsFilters()

  const hasFilters = hasFilterTags || !!search

  const { clearUrlQuery } = useClearUrlQuery()
  const handleClearFilters = () => {
    clearUrlQuery(['filter', 'filters', 'search'])
  }

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  const { identifier: workspaceId } = workspace

  const { data, loading, error, loadMore, totalCount } = useProject({
    workspaceId,
    projectId,
    search,
    filters,
  })

  // Load the document layout
  const { data: documentListSettingsData } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = documentListSettingsData || {}

  const isCurrentlyInProjectView =
    location.pathname ===
    routes.WORKSPACE_PROJECT.create({ workspaceId, projectId })

  const isCurrentlyInTrashProjectView =
    location.pathname ===
    routes.WORKSPACE_TRASH_PROJECT.create({ workspaceId, projectId })

  if (loading && !data.storageItems) {
    return <LoadingState />
  }

  const projectWorkspace = data.project?.workspace

  if (projectWorkspace && workspaceId !== projectWorkspace?.identifier) {
    return <ProjectNotFoundError />
  }

  if (error) {
    return (
      <ProjectCollectionError
        projectId={projectId}
        error={error}
        type="project"
      />
    )
  }

  const project = data.project
  const storageItems = data.storageItems
  const breadcrumbs = data.breadcrumbs

  const nestedProjects = storageItems?.filter(isProject) || []
  const shares = storageItems?.filter(isShare) || []

  const hasShares = !!shares.length
  const hasNestedProjects = !!nestedProjects.length

  /* ==========
   * Handle redirects when trashing / restoring projects
   * ==========
   * > Redirect to the Trash Project View if the project has been soft-deleted */

  if (!!project?.deletedAt && isCurrentlyInProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_TRASH_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  /*
   * > Redirect to the Project View if the project was restored */
  if (!project?.deletedAt && isCurrentlyInTrashProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }
  /* ==========
   * ========== */

  // Project Empty State
  if (!loading && project && totalCount.storageItems === 0) {
    return (
      <ProjectEmptyState
        workspace={workspace}
        project={project}
        breadcrumbs={breadcrumbs}
        hasFilters={hasFilters}
        onClearFilters={handleClearFilters}
      />
    )
  }

  const renderDocumentsList = () => {
    if (!hasShares || !project) {
      return null
    }

    const isGridLayout = documentsLayout === 'GRID'

    return (
      <DocumentsWrapper
        listLayout={!isGridLayout}
        // return list without gradient styling when there's no nested projects
        grouped={hasNestedProjects}
      >
        {/**
         * This header should only be seen when the user has both shares and
         * nested projects and is on the grid view
         */}
        {hasNestedProjects && isGridLayout && isTabletAndBigger && (
          <HeaderAfterCollections>
            Documents
            <Pill variant="secondary">{totalCount.shares}</Pill>
          </HeaderAfterCollections>
        )}
        {/* NOTE: do not render ProjectDocumentList conditionally within or without a wrapper
         * as this might cause bugs. See more: https://linear.app/sketch/issue/SWEB-380/creating-project-causes-modal-to-close-prematurely
         */}
        <ProjectDocumentList
          project={project}
          workspace={workspace}
          items={shares}
          totalCount={totalCount.shares}
          onLoadMore={loadMore.storageItems}
        />
      </DocumentsWrapper>
    )
  }

  const renderNestedProjectsList = () => {
    if (!hasNestedProjects) return null

    return (
      <>
        <NestedProjectItemsLayout
          workspaceId={workspaceId}
          nestedProjects={nestedProjects}
          onLoadMore={loadMore.storageItems}
          totalCount={totalCount.projects}
          disabledDrag={Boolean(project?.deletedAt)}
          renderDropdown={item => {
            const nestedProject = nestedProjects.find(
              p => p.identifier === item.identifier
            )

            if (!nestedProject) {
              ErrorHandler.shouldNeverHappen(
                'share should always be present rendering the ProjectDocumentDropdown'
              )
              return null
            }

            return (
              <NestedProjectDropdown
                workspace={workspace}
                nestedProject={nestedProject}
              />
            )
          }}
          extraPlaceholders={
            // In case we haven't reached the documents list, but we have exhausted the nested projects list
            // we want to add additional placeholders to the end of the list, so they would trigger the loading
            //
            // This is crucial when projects list length matches with the pagination length multiple.
            // E.g. if the pagination length is 20 and we have 20, 40, 60, etc. projects,
            hasShares ? 0 : totalCount?.storageItems - nestedProjects.length
          }
          customColumns={NESTED_PROJECTS_LAYOUT_COLUMNS}
        />
        <ScrollToTop />
      </>
    )
  }

  // For mobile view we want to show tabs
  if (isMobile && hasNestedProjects) {
    return (
      <MobileWrapper>
        <DocumentHead title={project?.name} />
        {project && (
          <ProjectHeader
            project={project}
            workspace={workspace}
            breadcrumbs={breadcrumbs}
            workspaceAccessLevel={project?.workspaceAccessLevel!}
          />
        )}
        <Tabs>
          <TabList>
            <Tab>
              Folders{' '}
              {totalCount.projects && (
                <Pill variant="secondary">{totalCount.projects}</Pill>
              )}
            </Tab>
            <Tab>
              Documents{' '}
              {totalCount.shares && (
                <Pill variant="secondary">{totalCount.shares}</Pill>
              )}
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelContent>
              {renderNestedProjectsList()}
              <Footer />
            </TabPanelContent>
          </TabPanel>
          <TabPanel>
            <TabPanelContent>{renderDocumentsList()}</TabPanelContent>
          </TabPanel>
        </Tabs>
      </MobileWrapper>
    )
  }

  return (
    <>
      <DocumentHead title={project?.name} />
      {project && (
        <ProjectHeader
          project={project}
          workspace={workspace}
          breadcrumbs={breadcrumbs}
          workspaceAccessLevel={project?.workspaceAccessLevel!}
        />
      )}
      {renderNestedProjectsList()}
      {renderDocumentsList()}
    </>
  )
}

export default ProjectShares
